import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { searchAppointmentSlots } from '../../../store/calendarService';
import { selectUsersForNames } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { stableSort } from '../../../utils/sortFunctions';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import AppointmentSlotsTableRow from './AppointmentSlotsTableRow';

const sortOrder = 'asc';
const headCells = [
    { id: 'date', label: 'Appointment Date' },
    { id: 'time', label: 'Time' },
    { id: 'location', label: 'Location', sortable: false },
    { id: 'adviser', label: 'Adviser' }
];

const AppointmentSlotsTableManagement = ({ acceptedRoles, filters, participantId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState({
        order: sortOrder,
        orderBy: 'lastStatusUpdated',
        page: 0,
        rowsPerPage: 100
    });

    // STORE STATE
    const users = useSelector(selectUsersForNames);
    const appointmentSlots = useSelector(
        (state) => state.entities.calendarService.appointmentSlotsSearch
    );
    const loadingAppointmentSlotsSearch = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentSlotsSearch
    );
    const appointmentSlotsSearchMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentSlotsSearchMetaData
    );

    // USEEFFECTS
    useEffect(() => {
        if (!appointmentSlotsSearchMetaData) return;
        if (
            !loadingAppointmentSlotsSearch &&
            appointmentSlots.length > 0 &&
            'number' in appointmentSlotsSearchMetaData &&
            !appointmentSlotsSearchMetaData.last
        ) {
            dispatch(searchAppointmentSlots(filters, appointmentSlotsSearchMetaData.number + 1));
        } else if (appointmentSlots?.length < 1 || users?.length < 1) setRows([]);
        else {
            const userIds = [...new Set(appointmentSlots?.map((el) => el.userId))].filter(
                (el) => el
            );
            const missingAdviserIds = userIds.filter(
                (el) => !users.find((entry) => entry.id === el)
            );
            missingAdviserIds.length > 0
                ? dispatch(loadUsersMinimalDetails(missingAdviserIds))
                : configRows();
        }
    }, [appointmentSlots, loadingAppointmentSlotsSearch, appointmentSlotsSearchMetaData, users]);

    // HELPER FNS
    const configRows = () => {
        const updatedRows = appointmentSlots.map((el) => {
            const adviser = users.find((entry) => entry.id === el.userId);
            return {
                ...el,
                time: `${el.startTime} - ${el.endTime}`,
                adviser: `${adviser?.firstName ?? ''} ${adviser?.lastName ?? ''}`
            };
        });
        setRows(updatedRows);
    };

    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <AppointmentSlotsTableRow
                key={el.id}
                row={el}
                acceptedRoles={acceptedRoles}
                newParticipantId={participantId}
            />
        ));
    };

    // RENDER
    return rows.length < 1 ? (
        <LoadingSpinner content="No appointment slots found" />
    ) : (
        <ResultsTable
            defaultOrderBy={rowMetaData.orderBy}
            defaultRowsPerPage={rowMetaData.rowsPerPage}
            headCells={headCells}
            passRowMetaDataUp={setRowMetaData}
            tableRows={createRows()}
            totalResults={rows.length}
        />
    );
};

AppointmentSlotsTableManagement.propTypes = {
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.object,
    participantId: PropTypes.string
};

export default AppointmentSlotsTableManagement;
